import React, { useState, useEffect } from "react";
import bgImage from "../../assets/loginBG.webp";
import rcsLogo from "../../assets/RCS-Logo-Blue.png";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch } from "react-redux";
import { loginUser, registerUser } from "../../store/auth-slice";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import supabase from "../../supabaseClient";
import { useNavigate } from "react-router-dom";
import api from "../../config/api";

const Login = () => {
  const [credentials, setCredentials] = useState({
    userName: "",
    password: "",
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  // Requestor Account Modal State
  const [showRequestorModal, setShowRequestorModal] = useState(false);
  const [requestorResultModal, setRequestorResultModal] = useState({
    open: false,
    status: "",
    title: "",
    message: "",
  });
  const [requestorPasswordVisible, setRequestorPasswordVisible] = useState(false);
  const [requestorLoading, setRequestorLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [departmentPrefixes, setDepartmentPrefixes] = useState({});
  const [requestorCredentials, setRequestorCredentials] = useState({
    firstName: "",
    lastName: "",
    userName: "",
    password: "",
    department: "",
    role: "",
  });
  const requestorRoles = ["Superadmin", "Employee", "Corporate", "Requestor"];

  // Fetch all departments on mount (same as user management)
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await api.get("/api/departments?status=active");
        const depts = response.data.departments || [];
        setDepartments(depts);

        // Build prefix mapping from fetched departments
        const prefixMap = {};
        depts.forEach(dept => {
          const normalizedName = dept.name.toLowerCase().replace(/\s+/g, '');
          prefixMap[normalizedName] = {
            default: dept.prefix || normalizedName,
            short: dept.short_prefix || dept.name.substring(0, 2).toLowerCase(),
            type: dept.type
          };
        });
        setDepartmentPrefixes(prefixMap);
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    };

    fetchDepartments();
  }, []);


  // Generate username (similar to CreateUser.jsx)
  const generateRequestorUsername = async (updatedCredentials) => {
    try {
      const { department, firstName, lastName, role } = updatedCredentials;
      if (!department) return;

      const normalizedDepartment = department.toLowerCase().replace(/\s+/g, "");
      const normalizedRole = (role || "").toLowerCase();

      let departmentPrefix;
      let searchPrefix;

      if (normalizedRole === "corporate") {
        // For corporate: cr##-[departmentPrefix]
        departmentPrefix = departmentPrefixes[normalizedDepartment]?.default || normalizedDepartment;
        searchPrefix = "cr";
      } else if (normalizedRole === "requestor") {
        // For requestor: rq##-[FirstNameLastName]
        searchPrefix = "rq";
      } else {
        // For other roles: use short prefix with name
        departmentPrefix = !firstName && !lastName
          ? departmentPrefixes[normalizedDepartment]?.default || "dept"
          : departmentPrefixes[normalizedDepartment]?.short || department.substring(0, 2).toLowerCase();
        searchPrefix = departmentPrefix;
      }

      // Fetch existing users to determine next number
      const { data: users, error } = await supabase
        .from("users")
        .select("user_name")
        .ilike("user_name", `${searchPrefix}%`);

      if (error) throw error;

      const existingUsers = users || [];
      const lastNumber =
        existingUsers
          .map((user) =>
            parseInt(user.user_name.replace(searchPrefix, "").split("-")[0], 10)
          )
          .filter((num) => !isNaN(num))
          .sort((a, b) => b - a)[0] || 0;

      let newUserName;
      if (normalizedRole === "corporate") {
        // cr01-terumo format
        newUserName = `${searchPrefix}${String(lastNumber + 1).padStart(2, "0")}-${departmentPrefix}`;
      } else if (normalizedRole === "requestor") {
        // rq01-FritzJannus format (FirstName + LastName combined, no spaces)
        // Helper to capitalize each word and remove spaces
        const formatName = (name) => {
          if (!name) return "";
          return name
            .trim()
            .split(/\s+/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join("");
        };
        const requestorFullName = `${formatName(firstName)}${formatName(lastName)}`;
        if (requestorFullName) {
          newUserName = `${searchPrefix}${String(lastNumber + 1).padStart(2, "0")}-${requestorFullName}`;
        } else {
          newUserName = `${searchPrefix}${String(lastNumber + 1).padStart(2, "0")}`;
        }
      } else if (!firstName && !lastName) {
        newUserName = `${departmentPrefix}${String(lastNumber + 1).padStart(2, "0")}`;
      } else {
        const formattedFullName = firstName && lastName
          ? `${firstName.trim().charAt(0).toUpperCase() + firstName.trim().slice(1).toLowerCase()}${lastName.trim().charAt(0).toUpperCase() + lastName.trim().slice(1).toLowerCase()}`
          : "";
        newUserName = `${departmentPrefix}${String(lastNumber + 1).padStart(2, "0")}-${formattedFullName}`;
      }

      setRequestorCredentials((prev) => ({
        ...prev,
        userName: newUserName,
      }));
    } catch (err) {
      console.error("Error generating username:", err);
      // Fallback: generate based on timestamp
      const timestamp = Date.now().toString().slice(-4);
      setRequestorCredentials((prev) => ({
        ...prev,
        userName: `user${timestamp}`,
      }));
    }
  };

  const handleRequestorInputChange = (e) => {
    const { name, value } = e.target;
    const updatedCredentials = { ...requestorCredentials, [name]: value };
    setRequestorCredentials(updatedCredentials);

    // Regenerate username when department, firstName, lastName, or role changes
    if (["department", "firstName", "lastName", "role"].includes(name) && updatedCredentials.department) {
      generateRequestorUsername(updatedCredentials);
    }
  };

  const handleRequestorSubmit = async (e) => {
    e.preventDefault();

    if (!requestorCredentials.department || !requestorCredentials.password || !requestorCredentials.role) {
      setRequestorResultModal({
        open: true,
        status: "failed",
        title: "Create Account Failed",
        message: "Please fill in all required fields.",
      });
      return;
    }

    if (requestorCredentials.password.length < 8) {
      setRequestorResultModal({
        open: true,
        status: "failed",
        title: "Create Account Failed",
        message: "Password must be at least 8 characters long.",
      });
      return;
    }

    setRequestorLoading(true);

    try {
      const result = await dispatch(
        registerUser({
          ...requestorCredentials,
          created_by_id: null,
          created_by_username: "self-registration",
          is_approved: false,
        })
      );

      if (result?.payload?.success) {
        setShowRequestorModal(false);
        setRequestorCredentials({
          firstName: "",
          lastName: "",
          userName: "",
          password: "",
          department: "",
          role: "",
        });
        setRequestorResultModal({
          open: true,
          status: "success",
          title: "Account Created",
          message: "Your requestor account has been created and is now pending approval.",
        });
      } else {
        setRequestorResultModal({
          open: true,
          status: "failed",
          title: "Create Account Failed",
          message: result?.payload?.message || "Failed to create account.",
        });
      }
    } catch (error) {
      console.error("Registration error:", error);
      setRequestorResultModal({
        open: true,
        status: "failed",
        title: "Create Account Failed",
        message: "Server error. Please try again later.",
      });
    } finally {
      setRequestorLoading(false);
    }
  };

  const togglePasswordVisibility = () => setPasswordVisible(!passwordVisible);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials({ ...credentials, [name]: value });
  };

  const handleLogin = (e) => {
    e.preventDefault();

    // Basic client-side validation
    if (!credentials.userName || !credentials.password) {
      toast.error("Please fill in both username and password.", {
        position: "top-right",
      });
      return;
    }

    setLoading(true);

    dispatch(loginUser(credentials))
      .then((data) => {
        console.log("Login Response Payload:", data);
        if (data.payload?.success) {
          toast.success(data.payload.message, { position: "top-right" });
          const role = String(data.payload?.user?.role || "").toLowerCase();
          if (role === "employee") {
            navigate("/admin/dashboard");
          } else if (role === "superadmin") {
            navigate("/superAdmin/super-dashboard");
          } else if (role === "corporate") {
            navigate("/corporate/corporate-dashboard");
          } else if (role === "requestor") {
            navigate("/requestor/dashboard");
          }
        } else {
          toast.error(
            data.payload?.message || "Incorrect username or password.",
            {
              position: "top-right",
            }
          );
        }
      })
      .catch((error) => {
        console.error("Login Error:", error);
        toast.error("Server error. Please try again later.", {
          position: "top-right",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <section
      id="login"
      className="relative min-h-screen flex items-center justify-center font-poppins bg-cover bg-center overflow-hidden"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      {/* Enhanced background overlay with gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue/70 via-blue/60 to-blue/80"></div>
      
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-yellow/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px]  rounded-full blur-3xl"></div>
      </div>

      {/* Modern glassmorphic container */}
      <div className="z-10 w-full max-w-5xl mx-auto px-4">
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden flex flex-col lg:flex-row animate-in fade-in-0 zoom-in-95 duration-500">
          
          {/* Left - Form Section */}
          <div className="w-full lg:w-1/2 p-8 sm:p-10 lg:p-12 flex flex-col justify-center">
            {/* Logo Section */}
            <div className="flex justify-center mb-8">
              <div className="p-4 bg-blue/5 rounded-2xl border border-blue/10">
                <img src={rcsLogo} alt="RCS Logo" className="w-44 sm:w-48" />
              </div>
            </div>

            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-blue mb-3">
                Welcome Back
              </h1>
              <p className="text-grey text-lg font-medium">
                Sign in to continue to your account
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleLogin} className="space-y-6">
              {/* Username Field */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-grey">
                  Username
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="fas fa-user text-blue/60 text-lg"></i>
                  </div>
                  <input
                    type="text"
                    name="userName"
                    value={credentials.userName}
                    onChange={handleInputChange}
                    placeholder="Enter your username"
                    autoComplete="off"
                    className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md text-base font-medium placeholder-grey/60"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-grey">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="fas fa-lock text-blue/60 text-lg"></i>
                  </div>
                  <input
                    type={passwordVisible ? "text" : "password"}
                    name="password"
                    value={credentials.password}
                    onChange={handleInputChange}
                    placeholder="Enter your password"
                    autoComplete="off"
                    className="w-full pl-12 pr-12 py-4 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md text-base font-medium placeholder-grey/60"
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-grey hover:text-blue transition-colors duration-200"
                  >
                    <i
                      className={`fas ${
                        passwordVisible ? "fa-eye" : "fa-eye-slash"
                      } text-lg`}
                    ></i>
                  </button>
                </div>
              </div>

              {/* Login Button */}
              <button
                type="submit"
                disabled={loading}
                className={`w-full py-4 rounded-2xl text-white font-bold text-base transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-xl flex items-center justify-center gap-3 ${
                  loading 
                    ? "bg-grey/50 cursor-not-allowed transform-none" 
                    : "bg-blue hover:from-blue/90 hover:to-blue"
                }`}
              >
                {loading ? (
                  <>
                    <div className="relative">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    </div>
                    <span>Signing In...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-sign-in-alt"></i>
                    <span>Sign In</span>
                  </>
                )}
              </button>
            </form>

            {/* Create Requestor Account Button */}
            <div className="mt-6">
              <button
                type="button"
                onClick={() => setShowRequestorModal(true)}
                className="w-full py-3 rounded-2xl border-2 border-blue text-blue font-semibold text-base transition-all duration-300 hover:bg-blue/5 hover:shadow-md flex items-center justify-center gap-2"
              >
                <i className="fas fa-user-plus"></i>
                <span>Create Requestor Account</span>
              </button>
            </div>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-center text-sm text-grey/70">
                Protected by enterprise-grade security
              </p>
            </div>
          </div>

          {/* Right - Info Section (Desktop) */}
          <div className="hidden lg:flex w-full lg:w-1/2 bg-blue text-white p-12 flex-col justify-center items-center relative overflow-hidden">
         
            
            <div className="relative z-10 text-center">
              {/* Icon */}
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-3xl flex items-center justify-center mb-8 mx-auto shadow-lg">
                <i className="fas fa-shield-alt text-4xl text-white"></i>
              </div>

              {/* Content */}
              <h2 className="text-2xl font-bold mb-4">
                Secure Access Portal
              </h2>
              <p className="text-white/90 text-base leading-relaxed mb-6 max-w-sm">
                This system is protected by advanced security protocols. 
                Your credentials are encrypted and monitored for security purposes.
              </p>

              {/* Features */}
              <div className="space-y-4 text-left">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-check text-white text-sm"></i>
                  </div>
                  <span className="text-white/90 text-sm">End-to-end encryption</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-check text-white text-sm"></i>
                  </div>
                  <span className="text-white/90 text-sm">Multi-factor authentication</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-check text-white text-sm"></i>
                  </div>
                  <span className="text-white/90 text-sm">24/7 security monitoring</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile info section */}
        <div className="lg:hidden mt-6 bg-white/80 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-lg">
          <div className="flex items-center gap-4 text-center">
            <div className="w-12 h-12 bg-blue/10 rounded-2xl flex items-center justify-center flex-shrink-0">
              <i className="fas fa-shield-alt text-blue text-xl"></i>
            </div>
            <div className="text-left">
              <h3 className="font-bold text-blue mb-1">Secure System Access</h3>
              <p className="text-sm text-grey/80">
                Enterprise-grade security with encrypted authentication
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Requestor Account Modal */}
      {showRequestorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowRequestorModal(false)}
          ></div>

          {/* Modal Content */}
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-in fade-in-0 zoom-in-95 duration-300">
            {/* Modal Header */}
            <div className="bg-blue px-6 py-5 rounded-t-3xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <i className="fas fa-user-plus text-white text-lg"></i>
                  </div>
                  <div>
                    <h2 className="text-white font-bold text-xl">Create Requestor Account</h2>
                    <p className="text-white/80 text-sm">Register as a corporate requestor</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowRequestorModal(false)}
                  className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                >
                  <i className="fas fa-times text-white text-lg"></i>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleRequestorSubmit} className="p-6 space-y-6">
              {/* Personal Info Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 bg-blue/10 rounded-lg flex items-center justify-center">
                    <i className="fas fa-id-card text-blue text-xs"></i>
                  </div>
                  <h3 className="text-blue font-semibold">Personal Information</h3>
                  <span className="text-grey/50 text-xs">(Optional)</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-grey mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={requestorCredentials.firstName}
                      onChange={handleRequestorInputChange}
                      placeholder="Enter first name"
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-grey mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={requestorCredentials.lastName}
                      onChange={handleRequestorInputChange}
                      placeholder="Enter last name"
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300"
                    />
                  </div>
                </div>
              </div>

              {/* Role & Department Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 bg-blue/10 rounded-lg flex items-center justify-center">
                    <i className="fas fa-building text-blue text-xs"></i>
                  </div>
                  <h3 className="text-blue font-semibold">Role & Department</h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-grey mb-2">
                      Role <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        name="role"
                        value={requestorCredentials.role}
                        onChange={handleRequestorInputChange}
                        required
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 appearance-none"
                      >
                        <option value="">Select role</option>
                        {requestorRoles.map((role, index) => (
                          <option key={index} value={role}>
                            {role}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <i className="fas fa-chevron-down text-grey text-sm"></i>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-grey mb-2">
                      Department <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        name="department"
                        value={requestorCredentials.department}
                        onChange={handleRequestorInputChange}
                        required
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 appearance-none"
                      >
                        <option value="">Select department</option>
                        {departments.map((dept, index) => (
                          <option key={index} value={dept.name}>
                            {dept.name}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <i className="fas fa-chevron-down text-grey text-sm"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Credentials Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 bg-blue/10 rounded-lg flex items-center justify-center">
                    <i className="fas fa-key text-blue text-xs"></i>
                  </div>
                  <h3 className="text-blue font-semibold">Account Credentials</h3>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-grey mb-2">
                    Username <span className="text-grey/50 text-xs font-normal">(Auto-generated)</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="userName"
                      value={requestorCredentials.userName}
                      disabled
                      className="w-full p-3 border-2 border-gray-200 rounded-xl bg-gray-50 text-grey cursor-not-allowed"
                      placeholder="Select organization first"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <i className="fas fa-magic text-grey/50 text-sm"></i>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-grey mb-2">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={requestorPasswordVisible ? "text" : "password"}
                      name="password"
                      value={requestorCredentials.password}
                      onChange={handleRequestorInputChange}
                      placeholder="Enter secure password"
                      required
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setRequestorPasswordVisible(!requestorPasswordVisible)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-grey hover:text-blue transition-colors"
                    >
                      {requestorPasswordVisible ? <FaEye size={16} /> : <FaEyeSlash size={16} />}
                    </button>
                  </div>
                  <p className="text-xs text-grey/70 mt-2 flex items-center gap-1">
                    <i className="fas fa-info-circle text-blue text-xs"></i>
                    Must be at least 8 characters
                  </p>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-4 border-t border-gray-100">
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowRequestorModal(false)}
                    className="flex-1 py-3 px-4 rounded-xl border-2 border-gray-200 text-grey font-semibold hover:bg-gray-50 transition-all duration-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={requestorLoading}
                    className={`flex-1 py-3 px-4 rounded-xl font-semibold text-white transition-all duration-300 flex items-center justify-center gap-2 ${
                      requestorLoading
                        ? "bg-grey/50 cursor-not-allowed"
                        : "bg-blue hover:bg-blue/90 shadow-lg hover:shadow-xl"
                    }`}
                  >
                    {requestorLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span>Creating...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-user-plus"></i>
                        <span>Create Account</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Requestor Result Modal */}
      {requestorResultModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setRequestorResultModal((prev) => ({ ...prev, open: false }))}
          ></div>

          <div className="relative w-full max-w-lg rounded-3xl bg-white shadow-2xl overflow-hidden animate-in fade-in-0 zoom-in-95 duration-300">
            <div
              className={`px-6 py-5 ${
                requestorResultModal.status === "success"
                  ? "bg-gradient-to-r from-blue to-blue/90"
                  : "bg-gradient-to-r from-red-600 to-red-500"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15">
                  <i
                    className={`text-white text-xl ${
                      requestorResultModal.status === "success"
                        ? "fas fa-check-circle"
                        : "fas fa-triangle-exclamation"
                    }`}
                  ></i>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    {requestorResultModal.title}
                  </h2>
                  <p className="text-white/80 text-sm">
                    {requestorResultModal.status === "success"
                      ? "Your requestor account is waiting for approval."
                      : "Please review the details and try again."}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <div
                className={`rounded-2xl px-4 py-4 ${
                  requestorResultModal.status === "success"
                    ? "border border-orange-100 bg-orange-50"
                    : "border border-red-100 bg-red-50"
                }`}
              >
                <p
                  className={`text-sm font-semibold ${
                    requestorResultModal.status === "success"
                      ? "text-orange-700"
                      : "text-red-700"
                  }`}
                >
                  {requestorResultModal.message}
                </p>
                {requestorResultModal.status === "success" && (
                  <p className="mt-1 text-sm text-orange-700/80">
                    You can sign in once your account has been approved.
                  </p>
                )}
              </div>

              <div className="flex items-start gap-3 rounded-2xl bg-gray-50 px-4 py-4">
                <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-xl bg-blue/10 text-blue">
                  <i className="fas fa-info-circle"></i>
                </div>
                <div className="text-sm text-grey leading-6">
                  {requestorResultModal.status === "success"
                    ? "We have saved your account request and it will appear in the Requestor Approval queue."
                    : "Double-check the form fields and try creating the account again."}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setRequestorResultModal((prev) => ({ ...prev, open: false }))}
                className={`w-full rounded-2xl px-4 py-3 font-semibold text-white shadow-lg transition-all duration-300 hover:shadow-xl ${
                  requestorResultModal.status === "success"
                    ? "bg-blue hover:bg-blue/90"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {requestorResultModal.status === "success" ? "Close" : "Try Again"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Login;
